import { useCallback, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { first } from "lodash";
import { reportError } from "src/services/errorReporting";
import { getChildTags } from "src/queries/tags.graphql";
import { useTagsHierarchyViewer } from "../context";
import { updateTagChildren } from "../actions/tagsActions";

/**
 * Custom hook for handling node toggle functionality in TagsHierarchyViewer
 * @param {Function} setToggledNodes - State setter for toggled nodes
 * @returns {Object} Object containing handleNodeToggled function and loadingNodeIds
 */
export const useHandleNodeToggle = (setToggledNodes) => {
  const { dispatch } = useTagsHierarchyViewer();
  const [loadingNodeIds, setLoadingNodeIds] = useState({});

  const [fetchChildTags] = useLazyQuery(getChildTags, {
    fetchPolicy: "cache-and-network",
    onCompleted: (response) => {
      const tagData = first(response?.childTags?.tags);
      if (tagData) {
        dispatch(updateTagChildren(tagData?.id, tagData?.childTags));
        setLoadingNodeIds((prev) => {
          const newState = { ...prev };
          delete newState[tagData.id];
          return newState;
        });
      }
    },
    onError: (error) => {
      reportError(error, { context: "Error fetching child tags" });
      // Clear loading state on error
      setLoadingNodeIds({});
    },
  });

  const handleNodeToggled = useCallback(
    (node) => {
      const { id, toggled } = node;

      setToggledNodes((prev) => ({
        ...prev,
        [id]: toggled,
      }));

      // Handle both EntityTag format (node.tag.tag) and direct Tag format (node.tag)
      const actualTag = node.tag?.tag || node.tag;

      // If node is being expanded and doesn't have children loaded yet, fetch them
      if (
        toggled &&
        !node.children?.length &&
        actualTag?.childTags?.total > 0
      ) {
        setLoadingNodeIds((prev) => ({ ...prev, [id]: true }));
        fetchChildTags({
          variables: {
            tagId: id,
          },
        });
      }
    },
    [setToggledNodes, fetchChildTags]
  );

  return { handleNodeToggled, loadingNodeIds };
};
